import { Component, EventEmitter, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import {
  DialogCloseResult,
  DialogContentBase,
  DialogRef,
  DialogService,
  DialogsModule,
  WindowCloseResult,
  WindowModule,
  WindowRef
} from '@progress/kendo-angular-dialog';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';

import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { ToastrService } from 'ngx-toastr';
import {
  faArrowDown,
  faArrowsRotate,
  faArrowUp,
  faBookOpen,
  faEdit,
  faEnvelope,
  faEye,
  faPlusCircle, faPrint
} from '@fortawesome/free-solid-svg-icons';

import { setTitle } from '../../common/utils.lib';
import {
  PlacementReview,
  PlacementReviewStudent,
  PlacementReviewStudentNew,
  PlacementReviewStudentType, User
} from '../../models/models.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { PlacementReviewStudentService } from '../placement-review-student.service';
import {
  PlacementReviewStudentAddComponent
} from '../placement-review-student-add.component/placement-review-student-add.component';
import { ConfirmModalComponent } from '../../common-components/confirm-modal/confirm-modal.component';
import { AssessmentComponent } from '../../assessment-form/assessment.component/assessment.component';
import { AssessmentService } from '../../assessment-form/assessment.service';
import { JsReportService } from '../../common-services/js-report.service';
import { PanelBarModule } from '@progress/kendo-angular-layout';
import { ModalWindowService } from '../../shared/modal-window.service';
import { PlacementReviewStudentAddWin } from '../placement-review-student-add-win/placement-review-student-add-win';

@Component({
  selector: 'casi-placement-review-student-list-win',
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent,
    PanelBarModule
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './placement-review-student-list-win.html',
  styleUrl: './placement-review-student-list-win.scss'
})
export class PlacementReviewStudentListWin implements OnInit, OnDestroy {
  private _placementReview: PlacementReview | undefined;

  @Input() title: string = 'Title Not Set';

  @Input({ required: true }) set placementReview(val: PlacementReview) {
    this._placementReview = val;
  }
  get placementReview(): PlacementReview | undefined {
    return this._placementReview;
  };

  @Output() onClose = new EventEmitter<void>();

  private _windowRef: WindowRef | undefined;

  public view: Observable<PlacementReviewStudent[]> = new Observable<PlacementReviewStudent[]>();
  public rawData: PlacementReviewStudent[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: PlacementReviewStudent[] = filterBy(this.rawData, this.filter);

  public currentItem: PlacementReviewStudentType = PlacementReviewStudentNew.create(this.placementReview);

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  public editWinOpen: Boolean = false;
  public formMode: FormModes = FormModes.NOTSET;
  private _subs: Subscription[] = [];

  statuses = ['Unavailable', 'Ready to start', 'Started', 'Completed'];

  columns = [
    { key: 'student_assessment_start_status', label: 'Student Self Assessment (Start)' },
    { key: 'student_assessment_mid_status', label: 'Student Self Assessment (Mid)' },
    { key: 'student_assessment_end_status', label: 'Student Self Assessment (End)' },
    { key: 'supervisor_assessment_mid_status', label: 'Supervisor Assessment (Mid)' },
    { key: 'supervisor_assessment_end_status', label: 'Supervisor Assessment (End)' }
  ];

  summary: any;


  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: PlacementReviewStudentService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private modalWindowService: ModalWindowService,
    private toastr: ToastrService,
    private assessmentService: AssessmentService,
    private jsReportService: JsReportService
  ) {
    faIcons.addIcons(
      faPlusCircle, faEdit, faArrowUp, faArrowDown, faArrowsRotate, faEye, faBookOpen, faPrint, faEnvelope
    );
  }

  public sort: SortDescriptor[] = [
    // { field: "student_institution_id", dir: "asc" },
    // { field: "student_email", dir: "asc" },
  ];

  //#region Ng Event handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    if (this.placementReview)
      this.dataService.query(this.placementReview.id);

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));

    this.loadSummary();
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion


  getValue(columnKey: string, status: string): number {
    return this.summary?.[columnKey]
      ?.find((x: any) => x.text === status)
      ?.value ?? 0;
  }

  //#region Grid functions

  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]).filter(v => v !== null && v !== undefined && v !== '');
  }

  //#endregion

  //#region Public functions

  setMidReviewVisible(dataItem: any): void {
    dataItem.supervisor_mid_review_visible = !dataItem.supervisor_mid_review_visible;
    this.logger.debug('setMidReviewVisible:dataItem: ', dataItem);
    this.currentItem = PlacementReviewStudent.create(dataItem);
    this.dataService.update(this.currentItem.id, this.currentItem)
      .then(resp => {
        this.logger.debug('saveActive:update:resp: ', resp);
        this.notify(dataItem.supervisor_mid_review_visible ? "Assessment visible to student" : "Assessment not visible to student");
        this.closeWindowRef();
        if (this.placementReview)
          this.dataService.query(this.placementReview.id);
      })
      .catch(err => {
        this.logger.error('setMidReviewVisible:update:err: ', err);
        this.notify("Error Updating Student", true);
      });
  }

  setEndReviewVisible(dataItem: any): void {
    dataItem.supervisor_end_review_visible = !dataItem.supervisor_end_review_visible;
    this.logger.debug('setEndReviewVisible:dataItem: ', dataItem);
    this.currentItem = PlacementReviewStudent.create(dataItem);
    this.dataService.update(this.currentItem.id, this.currentItem)
      .then(resp => {
        this.logger.debug('saveActive:update:resp: ', resp);
        this.notify(dataItem.supervisor_end_review_visible ? "Assessment visible to student" : "Assessment not visible to student");
        this.closeWindowRef();
        if (this.placementReview)
          this.dataService.query(this.placementReview.id);
      })
      .catch(err => {
        this.logger.error('setMidReviewVisible:update:err: ', err);
        this.notify("Error Updating Student", true);
      });
  }

  setActive(dataItem: any): void {
    dataItem.active = !dataItem.active;
    this.logger.debug('setActive:dataItem: ', dataItem);
    this.currentItem = PlacementReviewStudent.create(dataItem);
    this.saveActive(this.currentItem);
  }

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onClose.emit();
    // this.dialog.close({ text: CasiCloseOptions.Cancelled });
  }

  public onAddClick(titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onAddClick...");
    this.currentItem = PlacementReviewStudentNew.create(this.placementReview);
    this.formMode = FormModes.NEW;
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.showTitle = setTitle(this.formMode, 'STUDENT');
    this.openAddEditDialog(this.formMode, this.currentItem, titleBar);
  }

  onResendClick(dataItem: any): void {
    this.logger.debug("onResendClick: ", dataItem);
    this.currentItem = PlacementReviewStudent.create(dataItem);
    this.logger.debug("onResendClick:currentItem ", this.currentItem);
    const dialogRef = this.dialogService.open({
      content: ConfirmModalComponent,
      minWidth: 250,
      width: 550
    });

    const contentRef = dialogRef.content.instance as ConfirmModalComponent;
    contentRef.title = "CONFIRM RESEND EMAIL";
    contentRef.content = `Are you sure you want to resend the invitation email to ${this.currentItem.student_email}?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok && this.currentItem.student_email) {
        this.logger.debug("openConfirmDailog:ok to proceed... ");
        this.dataService.resendInvitation(this.currentItem)
          .then((resp: any) => {
            this.notify("Email Sent");
            if (this.placementReview)
              this.dataService.query(this.placementReview.id);
          }).catch((err: any) => {
            this.logger.error('reset:err: ', err);
            this.notify("Error Sending Email", true);
          });

      }
    });
  }

  onEditClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = PlacementReviewStudent.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.showTitle = setTitle(this.formMode, 'STUDENT');
    this.openAddEditDialog(this.formMode, this.currentItem, titleBar);
  }

  public printReport(dataItem: any, field: string): void {
    let reportWindow = window.open("about:blank");
    let title = `${this.getReportTitle(field)}`;
    let obj = { id: dataItem.getAssessmentId(field as keyof PlacementReviewStudent), title }
    this.jsReportService.showReportSpinnerPage(reportWindow);
    this.jsReportService.printAssessmentReport(obj)
      .then(report => {
        const url = URL.createObjectURL(report);
        this.jsReportService.generateReport(reportWindow, title, url);
      })
      .catch(e => {
        this.logger.error("printPDF", e);
        this.jsReportService.showReportErrorPage(reportWindow);
      });
  }

  public resetAssessment(dataItem: any, field: string): void {
    let assessmentId = dataItem.getAssessmentId(field as keyof PlacementReviewStudent);
    this.logger.debug('resetAssessment: assessmentId:', assessmentId);
    const dialogRef = this.dialogService.open({
      content: ConfirmModalComponent,
      minWidth: 250,
      width: 450
    });

    const contentRef = dialogRef.content.instance as ConfirmModalComponent;
    contentRef.title = "CONFIRM REOPEN";
    contentRef.content = `Are you sure you want to reset this assessment?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ");
        this.assessmentService.reset(dataItem.id, field)
          .then(resp => {
            this.logger.debug('reset:resp: ', resp);
            this.notify("Assessment Reset");
            if (this.placementReview)
              this.dataService.query(this.placementReview.id);
            this.loadSummary();
          })
          .catch(err => {
            this.logger.error('reset:err: ', err);
            this.notify("Error Resetting Assessment", true);
          });
      }
    });
  }

  public viewAssessment(dataItem: any, field: string): void {
    this.logger.debug("viewAssessment: ", { dataItem, field });
    this.openAssessmentDialog(dataItem, field, true);
  }

  public reopenAssessment(dataItem: any, field: string): void {
    let assessmentId = dataItem.getAssessmentId(field as keyof PlacementReviewStudent);
    this.logger.debug('reopenAssessment: assessmentId:', assessmentId);
    const dialogRef = this.dialogService.open({
      content: ConfirmModalComponent,
      minWidth: 250,
      width: 450
    });

    const contentRef = dialogRef.content.instance as ConfirmModalComponent;
    contentRef.title = "CONFIRM REOPEN";
    contentRef.content = `Are you sure you want to reopen this assessment?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ");
        this.assessmentService.reopen(assessmentId)
          .then(resp => {
            this.logger.debug('reopen: ', resp);
            this.notify("Assessment Opened");
            if (this.placementReview)
              this.dataService.query(this.placementReview.id);
            this.loadSummary();
          })
          .catch(err => {
            this.logger.error('reopen:err: ', err);
            this.notify("Error Opening Assessment", true);
          });
      }
    });
  }

  //#endregion

  //#region Private functions

  private setAddEditInstanceInputs(mode: FormModes, dataItem: PlacementReviewStudentType): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance as PlacementReviewStudentAddWin;
      inputs.student = dataItem;
      inputs.mode = mode;
      inputs.existingData = this.rawData;
      this.logger.debug('setAddEditInstanceInputsinputs: ', inputs);
    }
  }

  private openAddEditDialog(mode: FormModes, dataItem: PlacementReviewStudentType, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("openAddEditDialog...");
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      titleBarContent: titleBar,
      content: PlacementReviewStudentAddWin,
      minWidth: 320,
      width: 700,
    });
    //  const dialogRef = this.dialogService.open({
    //       content: PlacementReviewStudentAddComponent,
    //       minWidth: 320,
    //       width: 700,
    //     });

    this.setAddEditInstanceInputs(mode, dataItem);

    // const contentRef = dialogRef.content.instance as PlacementReviewStudentAddComponent;
    // contentRef.title = setTitle(mode, 'STUDENT');
    // contentRef.student = dataItem;
    // contentRef.mode = mode;
    // contentRef.existingData = this.rawData;

    this._windowRef.content.instance.onSubmit.subscribe((formData: FormGroup) => {
      this.logger.debug('openAddEditDialog:onSubmit:formData: ', formData);
      this.handleClose(formData.value, mode);
    });

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('openAddEditDialog:onClose');
      this.closeWindowRef();
    });

    // dialogRef.result.subscribe((r: any) => {
    //   this.logger.debug('openAddEditDialog:result:', r);
    //   if (r instanceof DialogCloseResult) {
    //     return;
    //   }
    //   if (r.text === CasiCloseOptions.Saved) {
    //     this.logger.debug("openAddEditDialog:saved... ", contentRef.editForm.value);
    //     this.handleClose(contentRef.editForm.value, mode);
    //   }
    // });
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data, mode);
    this.save(data, mode);
  }

  private openAssessmentDialog(dataItem: any, field: string, isDisabled: boolean): void {
    this.logger.debug("openAssessmentDialog: ", { dataItem, field });

    const dialogRef = this.dialogService.open({
      content: AssessmentComponent,
      minWidth: 320,
      width: 1600
    });

    const contentRef = dialogRef.content.instance as AssessmentComponent;
    contentRef.title = 'VIEW PLACEMENT REVIEW';
    contentRef.myReview = dataItem;
    contentRef.field = field;
    contentRef.isDisabled = isDisabled || false;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openAssessment:result:', r);
      if (this.placementReview)
        this.dataService.query(this.placementReview.id);
      if (r instanceof DialogCloseResult)
        return;
    });
  }

  private save(student: any, mode: FormModes): void {
    this.logger.debug('save...', { student, mode });
    if (mode === FormModes.EDIT) {
      this.dataService.updateStudent(student.id, student)
        .then(resp => {
          this.logger.debug('save:update:resp: ', resp);
          this.notify("Student Updated");
          this.closeWindowRef();
          if (this.placementReview)
            this.dataService.query(this.placementReview.id);
          this.loadSummary();
        })
        .catch(err => {
          this.logger.error('save:update:err: ', err);
          this.notify("Error Updating Student", true);
        });
    } else {
      this.dataService.add(student)
        .then(resp => {
          this.logger.debug('save:add:resp: ', resp);
          this.notify("Student Added");
          this.closeWindowRef();
          if (this.placementReview)
            this.dataService.query(this.placementReview.id);
          this.loadSummary();
        })
        .catch(err => {
          this.logger.error('save:add:err: ', err);
          this.notify("Error Creating Student", true);
        });
    }
  }

  private saveActive(student: any): void {
    this.logger.debug('saveActive...', student);
    this.dataService.update(student.id, student)
      .then(resp => {
        this.logger.debug('saveActive:update:resp: ', resp);
        this.notify(student.active ? "Student Activated" : "Student Deactivated");
        this.closeWindowRef();
        if (this.placementReview)
          this.dataService.query(this.placementReview.id);
      })
      .catch(err => {
        this.logger.error('saveActive:update:err: ', err);
        this.notify("Error Updating Student", true);
      });
  }

  private getReportTitle(field: string): string {
    const map: Record<string, string> = {
      start: 'Start Placement',
      mid: 'Mid Placement',
      end: 'End Placement'
    };
    const key = Object.keys(map).find(k => field.includes(k));
    return key ? map[key] : '';
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private closeWindowRef(): void {
    this.logger.debug(`closeWindowRef...`);
    if (this._windowRef) {
      this.modalWindowService.close(this._windowRef);
      this._windowRef = undefined;
    }
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  private loadSummary(): void {
    if (this.placementReview)
      this.dataService.getSummary(this.placementReview.id)
        .then(resp => {
          this.logger.debug('getSummary:resp: ', resp);
          this.summary = resp;
        })
        .catch(err => this.logger.error('getSummary:err: ', err));
  }

  //#endregion

}



